import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type ListResult, type Role, type ViewScope } from '../lib/api';
import { clearStaff, loadStaff } from '../lib/staff';
import { DataTable, ErrorBox, Pager, usePageSize, dash, useList, type Column, type Row } from '../components/DataTable';
import { ROLE_LABEL, SCREEN_LABEL, useMe } from '../lib/me';
import { useAutoSearch } from '../lib/search';
import LegacyManagerMap, { OwnerHandoverPanel, SettleScopePanel } from './LegacyManagerMap';
import { GRP_TITLE, ScreenPermSaveBar, TeamPermTable, UserGrpCell, UserScreenCell, useScreenPerms } from './ScreenPermPanel';
import DyopsTeamSync from './DyopsTeamSync';
import Modal from '../components/Modal';

/**
 * 탭(2026-10-13 way 「권한화면 통합 — 한눈에 · 너무 산별적」): 예전엔 사용자 표·화면 권한(팀별·개인별)·
 * 버스인 담당자 연결·담당 넘기기·정산 보기가 한 페이지에 세로로 쌓여 있었다. 사람 한 명의 권한
 * (역할·팀·조회 범위·화면 9개·기준정보 소속)을 **한 줄**에서 보고 고치도록 사용자 표와 개인별 표를 합쳤다.
 */
const TABS = [
  ['users', '사용자·권한'],
  ['managers', '버스인접수담당자 연결·넘기기'],
  ['scope', '버스인접수담당자별 정산 보기'],
] as const;
type Tab = (typeof TABS)[number][0];
const isTab = (v: unknown): v is Tab => TABS.some(([id]) => id === v);

const ROLES: Role[] = ['admin', 'dispatch', 'settle', 'accounting', 'view'];

/**
 * 조회 범위 — 접수·배차 목록에만 걸린다(정산은 회계가 전량을 봐야 해서 제외).
 * ⚠️ 'team' 은 팀이 지정된 계정에만 줄 수 있다. 팀이 없으면 사실상 'own' 인데 화면은
 *    team 이라고 말하게 되므로 서버가 막는다.
 */
const SCOPES: { id: ViewScope; label: string }[] = [
  { id: 'own',  label: '본인 담당' },
  { id: 'team', label: '소속 팀' },
  { id: 'all',  label: '전체' },
];

/**
 * 사용자 관리.
 *
 * 계정은 **초대하지 않는다** — 사내 도메인 계정이 처음 로그인하면 `조회` 권한으로 자동 등록되고,
 * 여기서 권한을 올리거나 비활성화한다. 그래서 "추가" 버튼이 없다.
 * 서버가 마지막 관리자 제거와 자기 강등을 막는다(잠김 방지).
 */
export default function Admin() {
  const { me } = useMe();
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('admin-users', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  const [nonce, setNonce] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [teams, setTeams] = useState<string[]>([]);

  // 이미 쓰이는 팀 이름 — 자동완성 목록. 실패해도 화면은 그대로 돌아간다(자유 입력이라).
  useEffect(() => {
    let cancelled = false;
    loadStaff()
      .then((r) => { if (!cancelled) setTeams(r.teams); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [nonce]);

  const { rows, loading, error } = useList(
    () => api.listAppUsers({ q: q || undefined, limit: PAGE, offset: page * PAGE }) as unknown as Promise<ListResult<Row>>,
    [q, page, PAGE, nonce],
  );

  async function apply(
    appUserId: number,
    patch: { role?: Role; isActive?: boolean; team?: string; viewScope?: ViewScope; viewTeams?: string[] },
  ) {
    setBusyId(appUserId);
    setSaveError(null);
    try {
      await api.setAppUserRole({ appUserId, ...patch });
      // 담당자 선택기가 이름·팀을 캐시한다 — 바꿨으면 다음에 다시 읽게 비운다.
      clearStaff();
      setNonce((n) => n + 1);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column[] = [
    { key: 'email', label: '이메일' },
    { key: 'name', label: '이름' },
    {
      key: 'role',
      label: '권한',
      render: (r) => {
        const id = Number(r.app_user_id);
        return (
          <select
            className="field w-28 py-1"
            value={String(r.role)}
            disabled={busyId === id}
            onChange={(e) => apply(id, { role: e.target.value as Role })}
          >
            {ROLES.map((x) => (
              <option key={x} value={x}>
                {ROLE_LABEL[x]}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      key: 'is_active',
      label: '상태',
      render: (r) => {
        const id = Number(r.app_user_id);
        const active = r.is_active === true;
        return (
          <button
            className="btn-ghost py-1 text-xs"
            disabled={busyId === id}
            onClick={() => apply(id, { isActive: !active })}
          >
            {active ? '사용중 · 비활성화' : '비활성 · 되살리기'}
          </button>
        );
      },
    },
    {
      key: 'team',
      label: '팀',
      // 자유 입력이라 자동완성이 없으면 '회계팀'과 '회계 팀'이 갈린다. 기존 값을 제시해 변종을 막는다.
      // 비우고 나가면 팀을 **지운다** — 서버는 빈 문자열과 미전송을 구분한다.
      render: (r) => {
        const id = Number(r.app_user_id);
        return (
          // 팀 정본은 DYOPS(dyopsTeamSync 매시 정각) — 여기서 바꿔도 DYOPS 에 팀이 있으면 다시 덮인다(10-29 dy@).
          <div>
            <input
              className="field w-28 py-1"
              list="team-list"
              defaultValue={String(r.team ?? '')}
              disabled={busyId === id}
              title="DYOPS 에 팀이 있으면 매시 정각 그 값으로 바뀝니다 — 팀은 DYOPS 에서 고치세요"
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next !== String(r.team ?? '')) apply(id, { team: next });
              }}
            />
            <div className="mt-0.5 text-[11px] text-ink-faint">DYOPS 에서 고침</div>
          </div>
        );
      },
    },
    {
      key: 'view_scope',
      label: '조회 범위',
      render: (r) => {
        const id = Number(r.app_user_id);
        // 관리자는 값과 무관하게 전량이라, 고를 수 있게 두면 안 먹는 설정처럼 보인다.
        if (r.role === 'admin') return <span className="text-xs text-ink-faint">전체 (관리자)</span>;
        // 팀 범위면 「함께 볼 팀」(10-28 · 복수) — 내 팀 말고 더 볼 팀을 고른다.
        const extra = ((r.view_teams as string[] | null | undefined) ?? []);
        const others = teams.filter((t) => t !== r.team);
        return (
          <div className="flex items-center gap-1">
            <select
              className="field w-28 py-1"
              value={String(r.view_scope ?? 'own')}
              disabled={busyId === id}
              onChange={(e) => apply(id, { viewScope: e.target.value as ViewScope })}
            >
              {SCOPES.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </select>
            {r.view_scope === 'team' && (
              // 10-28 way 캡처 「선택할 팀이 잘 안 보임」 — 표 스크롤 칸 안에 펼치면 잘린다 → 팝업으로.
              <ViewTeamsButton name={String(r.name ?? '')} team={String(r.team ?? '')} extra={extra} others={others} busy={busyId === id}
                               onChange={(next) => apply(id, { viewTeams: next })} />
            )}
          </div>
        );
      },
    },
    { key: 'updated_at', label: '변경일', render: (r) => String(r.updated_at ?? '—').slice(0, 16).replace('T', ' ') },
    { key: 'updated_by', label: '변경자', render: (r) => dash(r.updated_by) },
  ];

  // 화면 권한 칸(사용자 표 안) — 역할·팀을 바꾸면 nonce 로 다시 읽되 아직 저장 안 한 칸은 남긴다.
  const sp = useScreenPerms(nonce);
  const permColumns: Column[] = [
    ...sp.screens.map((s): Column => ({
      key: `scr_${s}`,
      label: SCREEN_LABEL[s],
      render: (r) => <UserScreenCell sp={sp} userId={Number(r.app_user_id)} screen={s} />,
    })),
    { key: 'grp_scope', label: '기준정보 수정 소속', render: (r) => <UserGrpCell sp={sp} userId={Number(r.app_user_id)} /> },
  ];
  const allColumns = [...columns.slice(0, -2), ...permColumns, ...columns.slice(-2)];

  const [params, setParams] = useSearchParams();
  const urlTab = params.get('tab');
  const tab: Tab = isTab(urlTab) ? urlTab : 'users';
  const setTab = (id: Tab) => setParams(id === 'users' ? {} : { tab: id }, { replace: true });
  const [teamOpen, setTeamOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1">
        <h1 className="mr-2 text-base font-bold" title="사내 계정이 처음 로그인하면 조회 권한으로 자동 등록됩니다.">
          사용자 관리
        </h1>
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
                  className={`rounded-lg px-2.5 py-1 text-sm font-medium ${tab === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
            {label}
          </button>
        ))}
        {me && <span className="ml-auto text-xs text-ink-faint">{me.email}</span>}
      </div>

      {tab === 'users' && (
        <>
          <details className="card p-3 text-sm text-ink-soft">
            <summary className="cursor-pointer font-medium text-ink">권한은 어떻게 정해지나요?</summary>
            <ul className="mt-2 list-disc space-y-1 break-keep pl-5">
              <li>사내 계정이 처음 로그인하면 <b>조회</b> 권한으로 자동 등록됩니다. 관리자는 모든 화면을 입력할 수 있습니다.</li>
              <li><b>화면 칸</b>(기준정보~공지사항): 개인 &gt; 팀 &gt; 역할 순으로 정해집니다. 비워 두면 팀·역할대로 따르고, 칸 아래 작은 글씨가 지금 적용되는 권한입니다. 하늘색 = 개인 예외, 노란색 = 아직 저장 안 한 칸.</li>
              <li><b>조회 범위</b>는 접수·배차 화면에 걸리고, 「버스인접수담당자별 정산 보기」를 켜면 정산 화면에도 걸립니다(영업관리자 = 본인 · 팀장 = 소속 팀 · 본부장 = 전체). 회계·관리자는 늘 전체이고, 담당자가 없는 접수는 모두에게 보입니다.</li>
              <li><b>{'기준정보 수정 소속'}</b>: {GRP_TITLE}</li>
            </ul>
          </details>

          <DyopsTeamSync onApplied={() => setNonce((n) => n + 1)} />

          <section className="card p-3">
            <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setTeamOpen((v) => !v)}>
              <span className="text-sm font-bold">
                팀별 기본 권한 <span className="font-normal text-ink-faint">— 팀 전체에 같은 권한을 줄 때 · 사람마다 다르게는 아래 표에서</span>
              </span>
              <span className="text-xs text-ink-faint">{teamOpen ? '접기 ▲' : `펼치기 ▼ (${sp.data?.teams.length ?? 0}팀)`}</span>
            </button>
            {teamOpen && <div className="mt-3"><TeamPermTable sp={sp} /></div>}
          </section>

          {/* 팀 자동완성 — 이미 쓰이는 값을 제시해 표기 변종('회계팀' vs '회계 팀')이 생기지 않게 한다. */}
          <datalist id="team-list">
            {teams.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setQ(term.trim());
              setPage(0);
            }}
            className="flex gap-2"
          >
            <input
              className="field w-72"
              placeholder="이메일 또는 이름"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            <button className="btn-ghost" type="submit">
              검색
            </button>
          </form>

          {error && <ErrorBox message={error} />}
          {saveError && <ErrorBox message={saveError} />}

          <DataTable columns={allColumns} rows={rows} loading={loading} tableId="admin.users" />
          <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
          <ScreenPermSaveBar sp={sp} />
        </>
      )}

      {tab === 'managers' && (
        <>
          <LegacyManagerMap />
          <OwnerHandoverPanel />
        </>
      )}

      {tab === 'scope' && <SettleScopePanel />}
    </div>
  );
}

/** 조회 범위 「팀」의 「+ 함께 볼 팀」 — 표 칸 안에서 펼치면 스크롤 상자에 잘려 팝업으로 고른다(10-28). 체크하면 바로 저장. */
function ViewTeamsButton({ name, team, extra, others, busy, onChange }: {
  name: string; team: string; extra: string[]; others: string[]; busy: boolean; onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="whitespace-nowrap rounded px-1.5 py-1 text-xs text-brand-700 hover:bg-canvas"
              title="내 팀 말고 함께 볼 팀" onClick={() => setOpen(true)}>
        {extra.length ? `+ ${extra.join('·')}` : '+ 함께 볼 팀'}
      </button>
      {open && (
        <Modal width="max-w-sm">
          <div className="card space-y-3 p-5">
            <h2 className="font-bold">{name} — 함께 볼 팀</h2>
            <p className="text-sm text-ink-soft">내 팀({team || '없음'}) 말고 더 볼 팀을 고르세요. 고르면 바로 저장됩니다.</p>
            <div className="space-y-1">
              {others.length === 0 && <div className="text-sm text-ink-faint">다른 팀이 없습니다</div>}
              {others.map((t) => (
                <label key={t} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-canvas">
                  <input type="checkbox" checked={extra.includes(t)} disabled={busy}
                         onChange={(e) => onChange(e.target.checked ? [...extra, t] : extra.filter((x) => x !== t))} />
                  {t}
                </label>
              ))}
            </div>
            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={() => setOpen(false)}>닫기</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
